import React from 'react';
import PropType from 'prop-types';
import {compose, mapProps} from 'recompose';
import {graphql} from 'react-apollo';
import gql from 'graphql-tag';
import ReactHighmaps from 'react-highcharts/ReactHighmaps.src';
import _ from 'lodash';
import europe from 'App/maps/europe.geo.json';
import world from 'App/maps/world.geo.json';
import * as helper from 'App/util/common/helper';
import {ButtonGroup, Button} from 'reactstrap';
import {renderForError, renderWhileLoading} from 'App/util/recompose/graphql';
import {process_country} from './helper';

const Map = {
  EUROPE: 0,
  WORLD: 1,
};

// Europe + Turkey
const europeIso2 = ['AX', 'AL', 'AD', 'AT', 'BY', 'BE', 'BA', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FO', 'FI', 'FR',
  'DE', 'GI', 'GR', 'GG', 'VA', 'HU', 'IS', 'IE', 'IM', 'IT', 'JE', 'LV', 'LI', 'LT', 'LU', 'MK', 'MT', 'MD', 'MC',
  'ME', 'NL', 'NO', 'PL', 'PT', 'XK', 'RO', 'RU', 'SM', 'RS', 'SK', 'SI', 'ES', 'SJ', 'SE', 'CH', 'UA', 'GB', 'TR'];

@compose(
  graphql(gql`
  query GeneralPatientDistributionAlive($order: OrderInput){
    generals_patients_distributions_alives(order:$order) {
      documenting_centre
      count
      country
      iso2
    }
  }`,
    {
      options: {
        variables: {
          order: {column: 'count', direction: 'desc'}
        }
      },
    }),
  renderWhileLoading(),
  renderForError(),
  mapProps(
    (ownerProps) => ({...ownerProps, data: ownerProps.data.generals_patients_distributions_alives})
  )
)
class PrevalenceMap extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      centres: null,
      map: Map.WORLD,
      onlyShowParticipating: false,
    };

    this.factor = 100000;

    const codes = _(this.props.data).uniqBy('iso2').map('iso2').value();

    let worldData = [];
    let europeData = [];
    let population = [];

    // map external centers
    const extData = process_country("centers");
    this.manipulatedData = this.props.data.map(data => {
      const centre = data.documenting_centre;
      if (centre in extData){
        return {...data, count: data.count + extData[centre]};
      }

      return data;
    });

    fetch('https://restcountries.com/v2/alpha?codes=' + codes.join(','))
      .then(function (response) {
        return response.json();
      })
      .then(function (myJson) {
        myJson.map(elem => {
          population[elem.alpha2Code] = elem.population;
        });

        worldData = _(this.manipulatedData)
          .groupBy('iso2')
          .map((objs, key) => {
            const iso2 = key.toUpperCase();
            const pop = population[key.toUpperCase()];
            const nPatient = _.sumBy(objs, 'count');
            const est = Math.round(11 / 100000 * pop);

            const value = (nPatient * this.factor) / pop;
            return {
              code: iso2,
              value: value,
              centres: objs,
              population: pop,
              estimation: est,
              N: nPatient
            }
          }).value();

        // remove all countries, that are not in Europe
        europeData = _(worldData)
          .map(_.clone)
          .remove((data) => (europeIso2.includes(data.code)))
          .value();


        this.setState({data: [europeData, worldData, population]});
      }.bind(this));

    this.N = _.sumBy(this.manipulatedData, 'count');

    // const codes = _.map(worldData, 'code');
    this.state.data = [europeData, worldData, population];
  }

  handleChangeMap(val) {
    const data = _(this.manipulatedData)
      .groupBy('iso2')
      .map((objs, key) => {
          // if (val === Map.EUROPE && !(key in europeIso2))
          //   return;

          // else
          return {
            'code': key.toUpperCase(),
            'value': _.sumBy(objs, 'count'),
            'centres': objs,
          }
        }
      )
      .value();
  }

  render() {
    const maps = [europe, world];
    const config = {
      chart: {
        paddingBottom: 20,
        spacingBottom: 20,
        height: 600
      },
      exporting: {enabled: false},
      title: {
        text: 'Prevalence Map'
      },

      subtitle: {
        text: `Living patients per ${this.factor} inhibitants (N = ${this.N})`// 'prevalence of alive patients with factor ' + this.factor,
      },

      legend: {
        layout: 'horizontal',
        borderWidth: 0,
        backgroundColor: 'rgba(255,255,255,0.85)',
        verticalAlign: 'bottom',
        y: 25,
        itemStyle: {
          textOverflow: 'none',
        }
      },

      colorAxis: {
        minColor: 'rgb(204, 236, 255)',
        maxColor: 'rgb(13, 150, 22)'
      },
      mapNavigation: {
        enabled: true,
        buttonOptions: {
          verticalAlign: 'bottom',
          align: 'right'
        }
      },

      plotOptions: {
        map: {
          allAreas: !this.state.onlyShowParticipating,
          joinBy: ['iso-a2', 'code'],
          dataLabels: {
            formatter: function () {
              return this.point.value ? ReactHighmaps.Highcharts.numberFormat(this.point.value, 2) : null
            },
            enabled: true,
            color: 'white',
            style: {
              fontWeight: 'bold'
            }
          },
          nullInteraction: false,
          mapData: ReactHighmaps.Highcharts.geojson(maps[this.state.map]),
        }
      },
      tooltip: {
        formatter: function () {
          if (this.point && this.point.value) {
            return '<b>' + this.point.name + '</b><br />' +
              'population: ' + this.point.population + '<br />' +
              'documented alive patients: ' + this.point.N;
          } else {
            return 'No centre found';
          }

        },
        shared: true
      },
      credits: {
        position: {
          verticalAlign: 'middle',
          align: 'center'
        },
        style: {
          fontSize: '20px'
        }
      },
      series: [{
        name: 'Countries',
        data: this.state.data[this.state.map],
        nullColor: 'grey',
        joinBy: ['iso-a2', 'code'],
        tooltip: {
          valueSuffix: ' patients'
        },
      }]
    };

    // head for table
    const head = ['#patients', 'Centre', 'Country'];

    return (
      <div>
        <ButtonGroup>
          <Button name="map"
                  active={this.state.map == Map.WORLD}
                  onClick={e => this.setState(helper.handleInputChange(e))}
                  value={Map.WORLD}>World</Button>
          <Button name="map"
                  active={this.state.map == Map.EUROPE}
                  onClick={e => this.setState(helper.handleInputChange(e))}
                  value={Map.EUROPE}>Europe</Button>
        </ButtonGroup>
        <ButtonGroup>
          <Button
            onClick={e => this.setState({onlyShowParticipating: !this.state.onlyShowParticipating})}
            active={this.state.onlyShowParticipating}>Only participating</Button>
        </ButtonGroup>
        <ReactHighmaps config={_.cloneDeep(config)}/>
        <ul>
          <li>Note: The map shows the minimal prevalence rates calculated from the number of documented PID
            patients in the ESID Database.
          </li>
        </ul>
      </div>
    );
  }
};

PrevalenceMap.propType = {
  data: PropType.array.isRequired,
  title: PropType.string.isRequired,
};

export default PrevalenceMap;


// WEBPACK FOOTER //
// ./src/views/Pages/Homepage/PrevalenceMap.js